import { useSignUp } from "@clerk/clerk-expo";
import { useToken } from "@gluestack-style/react";
import OAuthSignUp from "components/clerk/OAuthSignUp";
import { Box, Button, HStack, Icon, Image, Input, InputField, ParagraphSmall, Spinner, Typography } from "components/ui";
import { useRouter } from "expo-router";
import { Eye, EyeOff, TriangleAlert } from "lucide-react-native";
import React, { useState } from "react";
import { View } from "react-native";
import abcoLogo from "../assets/logo.svg";
interface SignUpProps {
	setIsSignUp: (isSignUp: boolean) => void;
}

const SignUp = ({ setIsSignUp }: SignUpProps) => {
	const { isLoaded, signUp, setActive } = useSignUp();
	const router = useRouter();

	const [pendingVerification, setPendingVerification] = React.useState(false);
	const [code, setCode] = React.useState("");
	const [emailAddress, setEmailAddress] = React.useState("");
	const [password, setPassword] = useState("");
	const [showPassword, setShowPassword] = useState(false);
	const [emailError, setEmailError] = useState("");
	const [passwordError, setPasswordError] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const resolvedRed = useToken("colors", "errormain");

	const isValidEmail = (email: string) => {
		const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		return re.test(String(email).toLowerCase());
	};
	const handleSignUp = async () => {
		if (!isValidEmail(emailAddress)) {
			setEmailError("Please enter a valid email address");
			return;
		}

		if (password.length < 6) {
			setPasswordError("Password must be at least 6 characters");
			return;
		}

		await onSignUpPress();
	};
	const onSignUpPress = async () => {
		setIsLoading(true);
		setEmailError("");
		setPasswordError("");

		if (!isLoaded) {
			setIsLoading(false);
			return;
		}

		try {
			await signUp.create({
				emailAddress,
				password,
			});

			// If we get here, the sign up was successful
			await signUp.prepareEmailAddressVerification({ strategy: "email_code" });
			setPendingVerification(true);
		} catch (err: any) {
			// Handle Clerk errors
			if (err.errors) {
				// Clerk API error pattern
				(err.errors as any[]).forEach((error: { meta?: { paramName?: string }; message: string }) => {
					const paramName = error.meta?.paramName;

					if (paramName === "email_address" || paramName === "emailAddress") {
						setEmailError(error.message);
					} else if (paramName === "password") {
						setPasswordError(error.message);
					} else {
						// For general errors, show under email field as common pattern
						setEmailError(error.message);
					}
				});
			} else {
				setEmailError("An unexpected error occurred. Please try again.");
				console.error("Sign up error:", err);
			}
		} finally {
			setIsLoading(false);
		}
	};

	const onPressVerify = async () => {
		if (!isLoaded) {
			return;
		}

		try {
			const completeSignUp = await signUp.attemptEmailAddressVerification({
				code,
			});

			if (completeSignUp.status === "complete") {
				await setActive({ session: completeSignUp.createdSessionId });
				router.replace("/");
			} else {
				console.error(JSON.stringify(completeSignUp, null, 2));
			}
		} catch (err: any) {
			// See https://clerk.com/docs/custom-flows/error-handling
			// for more info on error handling
			console.error(JSON.stringify(err, null, 2));
		}
	};
	return (
		<Box my={60}>
			<Box
				sx={{
					shadowColor: "#000",
					shadowOffset: {
						width: 0,
						height: 1,
					},
					shadowOpacity: 0.18,
					shadowRadius: 1.0,
					elevation: 1,
				}}
				h={"$full"}
				maxWidth={450}
				w={"$full"}
				borderRadius={12}
				paddingHorizontal={40}
				paddingVertical={32}
				marginInline="auto"
				bg="$neutral50"
				alignSelf="center"
			>
				<Box w={"$full"}>
					<Box gap={24} alignItems="center" justifyContent="center" mb={24}>
						<Box maxWidth={120}>
							<Image resizeMode="contain" source={abcoLogo} w={172} h={44} alt="Abco: The Power to Clean 9" style={{ width: 172, height: 44 }} />
						</Box>
						<Box alignItems="center">
							<Typography.H4>Create your account</Typography.H4>
							<ParagraphSmall sx={{ color: "$neutral600" }}>Welcome! Please fill in the details to get started.</ParagraphSmall>
						</Box>
					</Box>
					<Box>
						<OAuthSignUp layout="grid" />
					</Box>
					<Box gap={12} flexDirection="row" alignItems="center" width="100%" my="$5" mx="auto" px="$5">
						<Box flex={1} height="$px" bg="$neutral300" />
						<ParagraphSmall>or</ParagraphSmall>
						<Box flex={1} height="$px" bg="$neutral300" />
					</Box>
					<View>
						{!pendingVerification ? (
							<Box gap={16}>
								{/* Email Input */}
								<Box>
									<Input sx={{ paddingBlock: 12, paddingHorizontal: 6 }} bg="$basicwhite" borderRadius={8} borderColor="$neutral300" variant="outline" size="md">
										<InputField
											placeholder="Email"
											autoCapitalize="none"
											value={emailAddress}
											onChangeText={(text) => {
												setEmailAddress(text);
												setEmailError("");
											}}
											onBlur={() => {
												if (emailAddress && !isValidEmail(emailAddress)) {
													setEmailError("Please enter a valid email address");
												}
											}}
										/>
									</Input>

									{emailError && (
										<HStack width={"$full"} mt={24} padding={16} borderRadius={4} gap={8} alignItems="center" justifyContent="center" bg="$errorlight">
											<TriangleAlert style={{ minWidth: 16 }} color={resolvedRed} size={16} />
											<ParagraphSmall sx={{ color: "$errormain" }}>{emailError}</ParagraphSmall>
										</HStack>
									)}
								</Box>

								{/* Password Input */}
								<Box>
									<Input position="relative" sx={{ paddingBlock: 12, paddingHorizontal: 6 }} bg="$basicwhite" borderRadius={8} borderColor="$neutral300" variant="outline" size="md">
										<InputField
											placeholder="Password"
											secureTextEntry={!showPassword}
											value={password}
											onChangeText={(text) => {
												setPassword(text);
												setPasswordError("");
											}}
											onBlur={() => {
												if (password && password.length < 6) {
													setPasswordError("Password must be at least 6 characters");
												}
											}}
										/>
										<Button position="absolute" right={0} top={0} zIndex={5} variant="link" onPress={() => setShowPassword(!showPassword)} px="$3">
											<Icon as={showPassword ? EyeOff : Eye} color="$neutral500" />
										</Button>
									</Input>

									{passwordError && (
										<HStack width={"$full"} mt={24} padding={16} borderRadius={4} gap={8} alignItems="center" justifyContent="center" bg="$errorlight">
											<TriangleAlert style={{ minWidth: 16 }} color={resolvedRed} size={16} />
											<ParagraphSmall sx={{ color: "$errormain" }}>{passwordError}</ParagraphSmall>
										</HStack>
									)}
								</Box>

								{/* Sign Up Button */}
								<Button variant="primary" onPress={handleSignUp} disabled={isLoading} opacity={isLoading ? 0.7 : 1}>
									<Typography sx={{ color: "$neutral50" }}>{isLoading ? <Spinner color="$basicwhite" /> : "Create Account"}</Typography>
								</Button>
							</Box>
						) : (
							// Verification Code Input
							<Box gap={16}>
								<Input sx={{ paddingBlock: 12, paddingHorizontal: 6 }} bg="$basicwhite" borderRadius={8} borderColor="$neutral300" variant="outline" size="md">
									<InputField placeholder="Verification Code" value={code} onChangeText={setCode} autoCapitalize="none" />
								</Input>

								<Button variant="primary" onPress={onPressVerify} disabled={isLoading} opacity={isLoading ? 0.7 : 1}>
									<Typography sx={{ color: "$neutral50" }}>{isLoading ? "Verifying..." : "Verify Email"}</Typography>
								</Button>
							</Box>
						)}
					</View>
					<HStack justifyContent="center" alignItems="center" mt={24}>
						<ParagraphSmall>Already have an account?</ParagraphSmall>

						<Button sx={{ paddingHorizontal: 6 }} onPress={() => setIsSignUp(false)}>
							<ParagraphSmall sx={{ color: "#0066cc" }}>Sign In</ParagraphSmall>
						</Button>
					</HStack>
				</Box>
			</Box>
		</Box>
	);
};

export default SignUp;
